import React, { useState } from 'react';
import { X, BookOpen, ChevronRight, TrendingUp, ExternalLink, AlertTriangle, ShieldCheck, Info, Dna, FlaskConical, Microscope, ChevronDown, ChevronUp, Download, Lock, Shield, Upload, HardDrive, Eye, Server, FileText, Trash, Search } from 'lucide-react';

// ─── Shared style helpers (mirrors your app) ─────────────────────────────────
const categoryStyle = {
  'pathogenic':        'bg-rose-50    border-rose-300    text-rose-900',
  'likely-pathogenic': 'bg-orange-50  border-orange-300  text-orange-900',
  'risk':              'bg-amber-50   border-amber-300   text-amber-900',
  'protective':        'bg-emerald-50 border-emerald-300 text-emerald-900',
  'drug-response':     'bg-violet-50  border-violet-300  text-violet-900',
  'uncertain':         'bg-sky-50     border-sky-300     text-sky-900',
  'neutral':           'bg-slate-50   border-slate-200   text-slate-600',
};

// ─── Mini SNPCard (faithful replica of your real one) ────────────────────────
const MiniSNPCard = ({ rsid, gene, genotype, category, icon, riskLevel, primaryMessage, details = [], traits = [], annotate }) => (
  <div className="relative bg-white rounded-lg px-4 py-3.5 border border-slate-200 shadow-sm">
    {annotate && (
      <div className="absolute -top-3 -right-3 w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xs font-bold z-10 shadow">
        {annotate}
      </div>
    )}
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center flex-wrap gap-2 mb-2">
          <span className="text-sm font-bold text-slate-900">{rsid}</span>
          <span className="text-sm font-medium text-slate-500">{gene}</span>
          <span className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-xs font-mono font-semibold text-slate-600">{genotype}</span>
          <span className="text-base">{icon}</span>
          <span className={`px-2 py-0.5 rounded-md text-xs font-semibold border ${categoryStyle[category] || categoryStyle.neutral}`}>
            {riskLevel}
          </span>
          {traits.length > 0 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-violet-50 border border-violet-200 rounded-md text-xs font-medium text-violet-700">
              <TrendingUp className="w-2.5 h-2.5" /> GWAS
            </span>
          )}
        </div>
        <p className="text-sm text-slate-700 leading-relaxed">{primaryMessage}</p>
        {details.map((d, i) => (
          <p key={i} className="text-xs text-slate-500 mt-1">
            <span className="text-indigo-400 mr-1">→</span>{d}
          </p>
        ))}
        {traits.length > 0 && (
          <div className="flex items-center flex-wrap gap-1.5 mt-2">
            <BookOpen className="w-3 h-3 text-violet-500" />
            {traits.map((t, i) => (
              <span key={i} className="px-1.5 py-0.5 bg-violet-50 text-violet-700 text-xs rounded border border-violet-100">{t}</span>
            ))}
          </div>
        )}
      </div>
      <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
    </div>
  </div>
);

// ─── Callout box ─────────────────────────────────────────────────────────────
const Callout = ({ icon: Icon, color, title, children }) => {
  const colors = {
    blue:   'bg-sky-50   border-sky-200   text-sky-800',
    amber:  'bg-amber-50 border-amber-200 text-amber-800',
    green:  'bg-emerald-50 border-emerald-200 text-emerald-800',
    purple: 'bg-violet-50 border-violet-200 text-violet-800',
    slate:  'bg-slate-50  border-slate-200  text-slate-700',
  };
  return (
    <div className={`rounded-lg border p-3.5 ${colors[color]}`}>
      <div className="flex items-start gap-2">
        {Icon && <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />}
        <div>
          {title && <p className="text-xs font-bold mb-1">{title}</p>}
          <div className="text-xs leading-relaxed">{children}</div>
        </div>
      </div>
    </div>
  );
};

// ─── Expandable section ───────────────────────────────────────────────────────
const Expandable = ({ title, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
      >
        <span className="text-sm font-semibold text-slate-700">{title}</span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="px-4 py-3 space-y-3 text-sm text-slate-600 leading-relaxed border-t border-slate-200">{children}</div>}
    </div>
  );
};

// ─── Section header ───────────────────────────────────────────────────────────
const SectionHeader = ({ icon: Icon, color, children }) => (
  <div className="flex items-center gap-2 mb-3">
    <div className={`w-6 h-6 rounded flex items-center justify-center ${color}`}>
      <Icon className="w-3.5 h-3.5 text-white" />
    </div>
    <h3 className="text-base font-bold text-slate-800">{children}</h3>
  </div>
);

// ─── Understanding Results page ───────────────────────────────────────────────
const UnderstandingResultsPage = () => (
  <div className="space-y-8 text-sm">

    {/* Intro */}
    <div>
      <p className="text-slate-600 leading-relaxed mb-3">
        GENEie combines two independent databases to describe your variants: <strong>ClinVar</strong> (curated clinical evidence)
        and the <strong>GWAS Catalog</strong> (population-level research studies). Understanding where each label comes from helps you interpret what you're seeing.
      </p>
      <Callout icon={Info} color="blue" title="Educational use only">
        No result in GENEie is a diagnosis. Variants interact with each other, your environment, and many other factors.
        Always discuss findings with a genetic counselor or clinician.
      </Callout>
    </div>

    {/* 1 — Reading a result card */}
    <div>
      <SectionHeader icon={Dna} color="bg-indigo-500">Reading a result card</SectionHeader>
      <p className="text-slate-600 mb-4 leading-relaxed">
        Every variant in your results appears as a card. Here's what each part means:
      </p>

      <div className="relative mb-6">
        <MiniSNPCard
          rsid="rs429358"
          gene="APOE"
          genotype="CT"
          category="risk"
          icon="🟡"
          riskLevel="Risk Factor"
          primaryMessage="2.4x increased odds of Alzheimer's disease (1 copy of C)"
          details={["Associated with Late-onset Alzheimer's disease, Familial hypercholesterolemia"]}
          traits={["Alzheimer's disease", "LDL cholesterol"]}
        />
        {/* Annotation lines */}
        <div className="mt-3 grid grid-cols-2 gap-2">
          {[
            ['rsID', 'The unique identifier for this genetic position. You can look it up on dbSNP, ClinVar, or SNPedia.'],
            ['Gene', 'The gene this variant sits in. Variants in the same gene can have very different effects.'],
            ['Genotype', 'Your two alleles at this position — one from each parent.'],
            ['Category badge', 'Derived from ClinVar evidence and/or GWAS data. See the category guide below.'],
            ['Primary message', 'The most clinically significant finding for your specific genotype.'],
            ['GWAS pill', 'This variant has at least one GWAS population study association.'],
          ].map(([label, desc]) => (
            <div key={label} className="bg-slate-50 rounded-lg p-2.5 border border-slate-200">
              <p className="text-xs font-bold text-indigo-700 mb-0.5">{label}</p>
              <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* 2 — Category guide */}
    <div>
      <SectionHeader icon={FlaskConical} color="bg-rose-500">Category guide</SectionHeader>
      <p className="text-slate-600 mb-4 leading-relaxed">
        Categories come primarily from ClinVar's clinical review of published evidence. GWAS data can
        contribute a category only when no ClinVar entry exists.
      </p>
      <div className="space-y-2">
        {[
          { cat: 'pathogenic',        icon: '🔴', label: 'Pathogenic',       src: 'ClinVar',      desc: 'Strong peer-reviewed evidence that this variant causes a specific disease. This is a formal clinical classification, not a prediction.' },
          { cat: 'likely-pathogenic', icon: '🟠', label: 'Likely Pathogenic', src: 'ClinVar',      desc: 'Evidence is strong but not definitive. Classification may be updated as more data accumulates.' },
          { cat: 'risk',              icon: '🟡', label: 'Risk Factor',       src: 'ClinVar / GWAS', desc: 'Associated with increased risk of a condition, but does not cause it directly. Many people with this variant never develop the condition.' },
          { cat: 'protective',        icon: '🟢', label: 'Protective / Benign', src: 'ClinVar',    desc: 'ClinVar classifies this variant as benign or likely benign — no known harmful effect. Can also reflect a GWAS protective association.' },
          { cat: 'drug-response',     icon: '💊', label: 'Drug Response',     src: 'ClinVar',      desc: 'Affects how you metabolise or respond to a specific medication. Relevant to discuss with a prescriber.' },
          { cat: 'uncertain',         icon: '🔵', label: 'Uncertain',         src: 'ClinVar',      desc: 'Evidence exists but experts disagree, or there is not yet enough data to classify. ClinVar explicitly marks these as VUS (Variant of Uncertain Significance).' },
          { cat: 'neutral',           icon: '⚪', label: 'Neutral',           src: 'GWAS only',    desc: 'A GWAS study found an association, but the effect size is small (OR 0.83–1.2) and no ClinVar entry exists. Not considered clinically significant.' },
        ].map(({ cat, icon, label, src, desc }) => (
          <div key={cat} className="flex gap-3 p-3 bg-white rounded-lg border border-slate-200">
            <span className="text-xl flex-shrink-0 mt-0.5">{icon}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded border ${categoryStyle[cat]}`}>{label}</span>
                <span className="text-xs text-slate-400">Source: {src}</span>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* 3 — ClinVar vs GWAS */}
    <div>
      <SectionHeader icon={Microscope} color="bg-purple-500">ClinVar vs GWAS — what's the difference?</SectionHeader>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-xs font-bold text-blue-800 mb-1">ClinVar</p>
          <ul className="text-xs text-blue-700 space-y-1 leading-relaxed">
            <li>• Curated by clinical labs and expert panels</li>
            <li>• Based on family studies, functional experiments, case reports</li>
            <li>• Single variant → single disease relationship</li>
            <li>• High confidence when reviewed by expert panel</li>
          </ul>
        </div>
        <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
          <p className="text-xs font-bold text-violet-800 mb-1">GWAS Catalog</p>
          <ul className="text-xs text-violet-700 space-y-1 leading-relaxed">
            <li>• Population studies comparing thousands of people</li>
            <li>• Statistical associations, not causal proofs</li>
            <li>• Effect sizes are often small (OR 1.1–1.5)</li>
            <li>• Results vary across ancestries and cohorts</li>
          </ul>
        </div>
      </div>
      <Callout icon={Info} color="purple" title="Why the same variant can appear in both">
        A variant can be <strong>pathogenic for a rare disease</strong> (ClinVar) and <em>also</em> show a
        population-level association with an unrelated quantitative trait like BMI (GWAS). These are
        independent findings. The ClinVar classification always takes priority for the category badge —
        the GWAS data is shown as additional context only.
      </Callout>
    </div>

    {/* 4 — What GWAS numbers mean */}
    <div>
      <SectionHeader icon={TrendingUp} color="bg-amber-500">Understanding GWAS numbers</SectionHeader>

      {/* Mock detail card GWAS section */}
      <p className="text-slate-600 mb-3 leading-relaxed">
        When you open a variant's detail view, GWAS associations appear like this:
      </p>
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200 p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <BookOpen className="w-4 h-4 text-purple-600" /> GWAS Associations
          </p>
          <span className="text-xs text-purple-600 font-medium">3 total studies</span>
        </div>
        {[
          {
            trait: 'Type 2 diabetes',
            badge: { copies: 1, label: '1 copy', color: 'amber' },
            riskAllele: 'C',
            direction: 'increased',
            dirLabel: '↑ 28% higher odds',
            dirColor: 'text-red-600',
            pValue: '4.2e-12',
            isOR: true,
            annotate: 'A',
          },
          {
            trait: 'Body mass index',
            badge: { copies: 2, label: '2 copies', color: 'red' },
            riskAllele: 'T',
            direction: 'decreased',
            dirLabel: '↓ lower',
            dirColor: 'text-green-600',
            pValue: '3.1e-8',
            isOR: false,
            annotate: 'B',
          },
          {
            trait: "Alzheimer's disease",
            badge: { copies: 0, label: 'Not carried', color: 'green' },
            riskAllele: 'A',
            direction: null,
            dirLabel: null,
            dirColor: null,
            pValue: '7.8e-22',
            isOR: true,
            annotate: 'C',
          },
        ].map((a, i) => (
          <div key={i} className="relative bg-white rounded-lg p-3 border border-purple-100 mb-2">
            <div className="absolute -top-2 -left-2 w-5 h-5 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xs font-bold shadow">
              {a.annotate}
            </div>
            <div className="flex items-start justify-between gap-2 mb-1.5">
              <p className="text-xs font-semibold text-slate-900">{a.trait}</p>
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium border ${
                a.badge.copies === 0 ? 'bg-green-50 border-green-200 text-green-700' :
                a.badge.copies === 1 ? 'bg-amber-50 border-amber-200 text-amber-700' :
                'bg-red-50 border-red-200 text-red-700'
              }`}>
                {a.badge.copies === 0 ? <ShieldCheck className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                {a.badge.label}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-xs text-purple-700 font-mono bg-purple-50 px-1.5 py-0.5 rounded">Risk allele: {a.riskAllele}</span>
              {a.dirLabel && <span className={`text-xs font-medium ${a.dirColor}`}>{a.dirLabel}</span>}
              <span className="text-xs text-slate-400">p = {a.pValue}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Annotation explanations */}
      <div className="space-y-2 mb-4">
        {[
          ['A', 'amber', 'You carry 1 copy of the risk allele — the association applies to you. The effect is expressed as an odds ratio (OR): 28% higher odds means the studied population with this allele had 1.28× the rate of Type 2 diabetes compared to those without it. This does not mean a 28% chance of developing diabetes.'],
          ['B', 'red', 'You carry 2 copies, but the number shown (↓ lower) has no percentage because this is a quantitative trait. GWAS studies of continuous measurements like BMI report beta coefficients — effect sizes in standard deviation units — not odds ratios. Displaying a percentage would be meaningless without knowing the SD of BMI in the original cohort.'],
          ['C', 'green', '"Not carried" means your genotype does not include the studied risk allele. The association is shown for completeness but does not apply to you personally.'],
        ].map(([label, color, desc]) => (
          <div key={label} className="flex gap-3 items-start">
            <div className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold text-white shadow ${
              color === 'amber' ? 'bg-amber-500' : color === 'red' ? 'bg-red-500' : 'bg-green-500'
            }`}>{label}</div>
            <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>

      <Callout icon={AlertTriangle} color="amber" title="Odds ratios are not probabilities">
        An OR of 1.28 does not mean a 28% chance of disease. It means the rate in the study group
        was 1.28× the rate in the reference group. If the baseline rate is 5%, your rate would be ~6.4% — a
        real difference, but not 28%.
      </Callout>
    </div>

    {/* 5 — FAQ */}
    <div>
      <SectionHeader icon={Info} color="bg-slate-500">Common questions</SectionHeader>
      <div className="space-y-2">
        <Expandable title="Why does the same variant show as both Pathogenic and have a GWAS association?">
          <p>These come from different databases measuring different things. ClinVar's pathogenic classification reflects clinical evidence that the variant causes a specific disease. A GWAS association might show the same variant is also statistically correlated with an unrelated trait like BMI across a large population — these are independent findings that don't contradict each other. The category badge always reflects ClinVar when it exists.</p>
        </Expandable>
        <Expandable title="What does 'Conflicting classifications of pathogenicity' mean?">
          <p>Different clinical laboratories have submitted different interpretations to ClinVar. One lab may classify it as pathogenic, another as uncertain. This is common for variants where evidence is limited or studies have disagreed. It does not mean the variant is harmless — it means the scientific community hasn't reached consensus yet.</p>
        </Expandable>
        <Expandable title="Why don't some GWAS entries show a percentage?">
          <p>GWAS studies of <strong>quantitative traits</strong> (BMI, cholesterol levels, blood pressure, etc.) report <strong>beta coefficients</strong> — the average change in the measurement per allele, in standard deviation units. These are not odds ratios and cannot be expressed as "X% higher odds." GENEie suppresses the number and shows directional text only (↑ higher / ↓ lower) to avoid presenting a meaningless figure.</p>
        </Expandable>
        <Expandable title="Should I be worried about a Pathogenic result?">
          <p>Not necessarily without more context. "Pathogenic" means the variant is causally linked to a condition in the medical literature — but penetrance (the proportion of people with the variant who actually develop the disease) varies enormously. Some pathogenic variants have near-100% penetrance; others are much lower. The associated conditions listed with the variant and a conversation with a genetic counselor will give you much more useful context than the badge alone.</p>
        </Expandable>
        <Expandable title="Why might my results differ from a previous genetic test?">
          <p>ClinVar classifications are updated regularly as new evidence emerges. A variant classified as Uncertain Significance (VUS) in 2019 may now be Pathogenic — or downgraded to Benign. GENEie always uses the current database. Additionally, different testing companies use different SNP arrays and may not test every position.</p>
        </Expandable>
      </div>
    </div>

  </div>
);


// ─── Getting Your Data page ───────────────────────────────────────────────────
const GettingYourDataPage = () => (
  <div className="space-y-8 text-sm">

    <div>
      <p className="text-slate-600 leading-relaxed mb-3">
        GENEie works with raw DNA data files from consumer genetic testing services.
        These files contain your genotype at hundreds of thousands of positions across
        your genome — the same data that powers your ancestry and health reports,
        just in a portable format you can download and use yourself.
      </p>
      <Callout icon={Info} color="blue" title="What file format does GENEie need?">
        GENEie accepts the standard tab-separated raw data format (.txt) exported by
        23andMe, AncestryDNA, MyHeritage, and most other major providers. Each row
        contains an rsID, chromosome, position, and your two alleles at that position.
      </Callout>
    </div>

    {/* Provider instructions */}
    <div>
      <SectionHeader icon={Download} color="bg-indigo-500">How to download your raw data</SectionHeader>

      <div className="space-y-3">

        {/* 23andMe */}
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-slate-200">
            <span className="text-xl">🧬</span>
            <div>
              <p className="text-sm font-bold text-slate-800">23andMe</p>
              <p className="text-xs text-slate-500">most common format — works directly</p>
            </div>
          </div>
          <ol className="px-4 py-3 space-y-2 text-xs text-slate-600 leading-relaxed list-decimal list-inside marker:text-indigo-400 marker:font-bold">
            <li>Sign in at <span className="font-mono bg-slate-100 px-1 rounded">23andme.com</span></li>
            <li>Click your name in the top-right → <strong>Settings</strong></li>
            <li>Scroll to <strong>23andMe Data</strong> → click <strong>View</strong></li>
            <li>Select <strong>Download Raw Data</strong></li>
            <li>Choose <strong>All DNA Raw Data</strong> and confirm with your password</li>
            <li>You'll receive an email with a download link — the file will be a <span className="font-mono bg-slate-100 px-1 rounded">.zip</span> containing a <span className="font-mono bg-slate-100 px-1 rounded">.txt</span> file</li>
            <li>Extract the <span className="font-mono bg-slate-100 px-1 rounded">.txt</span> file and upload it to GENEie</li>
          </ol>
          <div className="px-4 pb-3">
            <Callout icon={Info} color="slate" title="">
              23andMe v3, v4, and v5 chip formats are all supported. The file will begin with comment lines starting with <span className="font-mono">#</span>, which GENEie skips automatically.
            </Callout>
          </div>
        </div>

        {/* AncestryDNA */}
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-slate-200">
            <span className="text-xl">🌳</span>
            <div>
              <p className="text-sm font-bold text-slate-800">AncestryDNA</p>
              <p className="text-xs text-slate-500">works directly after extraction</p>
            </div>
          </div>
          <ol className="px-4 py-3 space-y-2 text-xs text-slate-600 leading-relaxed list-decimal list-inside marker:text-indigo-400 marker:font-bold">
            <li>Sign in at <span className="font-mono bg-slate-100 px-1 rounded">ancestry.com</span></li>
            <li>Go to <strong>DNA</strong> in the top navigation → <strong>Your DNA Results Summary</strong></li>
            <li>Click <strong>Settings</strong> (top-right of the DNA page)</li>
            <li>Scroll to <strong>Download DNA Raw Data</strong> → click <strong>Download</strong></li>
            <li>Confirm with your password; you'll receive an email with a download link</li>
            <li>The download is a <span className="font-mono bg-slate-100 px-1 rounded">.zip</span> — extract the <span className="font-mono bg-slate-100 px-1 rounded">.txt</span> inside and upload it</li>
          </ol>
        </div>

        {/* MyHeritage */}
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-violet-50 to-purple-50 border-b border-slate-200">
            <span className="text-xl">🏛️</span>
            <div>
              <p className="text-sm font-bold text-slate-800">MyHeritage DNA</p>
              <p className="text-xs text-slate-500">works directly</p>
            </div>
          </div>
          <ol className="px-4 py-3 space-y-2 text-xs text-slate-600 leading-relaxed list-decimal list-inside marker:text-indigo-400 marker:font-bold">
            <li>Sign in at <span className="font-mono bg-slate-100 px-1 rounded">myheritage.com</span></li>
            <li>Go to <strong>DNA</strong> → <strong>Manage DNA Kits</strong></li>
            <li>Click the three-dot menu next to your kit → <strong>Download DNA data</strong></li>
            <li>Confirm via email, then download the <span className="font-mono bg-slate-100 px-1 rounded">.zip</span> and extract the <span className="font-mono bg-slate-100 px-1 rounded">.csv</span> file</li>
          </ol>
          <div className="px-4 pb-3">
            <Callout icon={AlertTriangle} color="amber" title="">
              MyHeritage exports a <span className="font-mono">.csv</span> (comma-separated) file rather than tab-separated. GENEie handles both formats automatically.
            </Callout>
          </div>
        </div>

        {/* FamilyTreeDNA */}
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-slate-200">
            <span className="text-xl">🌿</span>
            <div>
              <p className="text-sm font-bold text-slate-800">FamilyTreeDNA</p>
              <p className="text-xs text-slate-500">works directly</p>
            </div>
          </div>
          <ol className="px-4 py-3 space-y-2 text-xs text-slate-600 leading-relaxed list-decimal list-inside marker:text-indigo-400 marker:font-bold">
            <li>Sign in at <span className="font-mono bg-slate-100 px-1 rounded">familytreedna.com</span></li>
            <li>Go to <strong>My Kit</strong> → <strong>Download Raw Data</strong></li>
            <li>Select <strong>Concatenated Raw Data</strong> for the most complete file</li>
            <li>Extract the zip and upload the <span className="font-mono bg-slate-100 px-1 rounded">.csv</span> file</li>
          </ol>
        </div>

        {/* Nebula / Dante / WGS */}
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
            <span className="text-xl">🔭</span>
            <div>
              <p className="text-sm font-bold text-slate-800">Whole Genome Sequencing (Nebula, Dante, etc.)</p>
              <p className="text-xs text-slate-500">requires conversion first</p>
            </div>
          </div>
          <div className="px-4 py-3 text-xs text-slate-600 leading-relaxed space-y-2">
            <p>
              WGS providers deliver a <span className="font-mono bg-slate-100 px-1 rounded">.vcf</span> (Variant Call Format) file,
              not the SNP chip format GENEie expects. You'll need to convert it first using a tool
              like <strong>BCFtools</strong> or an online VCF-to-23andMe converter before uploading.
            </p>
            <Callout icon={Info} color="slate" title="">
              Native VCF support is on our roadmap. For now, use an intermediate conversion step.
            </Callout>
          </div>
        </div>

      </div>
    </div>

    {/* What the file looks like */}
    <div>
      <SectionHeader icon={Dna} color="bg-slate-500">What the file looks like inside</SectionHeader>
      <p className="text-slate-600 mb-3 leading-relaxed">
        All supported files share the same basic structure — comment lines at the top, then one row per SNP:
      </p>
      <div className="bg-slate-900 rounded-lg p-4 font-mono text-xs overflow-x-auto">
        <p className="text-slate-500"># This data file generated by 23andMe</p>
        <p className="text-slate-500"># Genome build: GRCh37</p>
        <p className="text-slate-500"># rsid    chromosome  position  genotype</p>
        <p className="text-emerald-400">rs4477212    1    82154    AA</p>
        <p className="text-emerald-400">rs3094315    1    752566   AG</p>
        <p className="text-emerald-400">rs429358     19   45411941 CT</p>
        <p className="text-slate-500 mt-1"># ... ~600,000 more rows</p>
      </div>
      <p className="text-xs text-slate-400 mt-2 leading-relaxed">
        GENEie reads the rsID and genotype columns. Chromosome and position are used for display only.
        Lines with genotype <span className="font-mono bg-slate-100 px-1 rounded">--</span> (no call) are automatically skipped.
      </p>
    </div>

    {/* FAQ */}
    <div>
      <SectionHeader icon={Info} color="bg-slate-500">Common questions</SectionHeader>
      <div className="space-y-2">
        <Expandable title="My file is very large — is that normal?">
          <p>Yes. A typical 23andMe or AncestryDNA raw data file contains thousands of SNPs and is usually 20–40 MB uncompressed. GENEie is designed to handle this and will process the entire file, though analysis may take a minute or two depending on your connection.</p>
        </Expandable>
        <Expandable title="What if my provider isn't listed here?">
          <p>If your provider exports a tab-separated or comma-separated file with columns for rsID, chromosome, position, and genotype, it will almost certainly work. Try uploading it — GENEie will parse what it can and ignore lines it doesn't recognise.</p>
        </Expandable>
        <Expandable title="Can I upload a file from a relative?">
          <p>You should only upload genetic data for yourself or someone who has explicitly consented. Genetic data is deeply personal and can reveal information about family members who have not consented to testing.</p>
        </Expandable>
        <Expandable title="My download is a .zip — do I need to extract it first?">
          <p>Yes — GENEie currently requires the extracted text file, not the zip. Extract the archive on your computer first, then upload the <span className="font-mono text-xs bg-slate-100 px-1 rounded">.txt</span> or <span className="font-mono text-xs bg-slate-100 px-1 rounded">.csv</span> file inside.</p>
        </Expandable>
      </div>
    </div>

  </div>
);

// ─── Privacy & Security page ──────────────────────────────────────────────────
const PrivacySecurityPage = () => (
  <div className="space-y-8 text-sm">

    <div>
      <p className="text-slate-600 leading-relaxed mb-3">
        Your genetic data is among the most sensitive personal information that exists.
        This page explains exactly what happens to your file when you upload it, where it goes,
        and what controls you have over it.
      </p>
      <Callout icon={ShieldCheck} color="green" title="Short version">
        Your raw DNA file is processed locally in your browser first. It is only uploaded to our
        servers if you explicitly choose to save your analysis. You are always in control.
      </Callout>
    </div>

    {/* Upload flow */}
    <div>
      <SectionHeader icon={Upload} color="bg-indigo-500">How the upload process works</SectionHeader>
      <p className="text-slate-600 mb-4 leading-relaxed">
        Here is exactly what happens, step by step, when you upload a file:
      </p>

      <div className="space-y-2">
        {[
          {
            step: '1',
            color: 'bg-indigo-500',
            title: 'File is read in your browser',
            icon: HardDrive,
            desc: 'When you select a file, your browser reads it directly from your disk into memory. The file does not leave your device at this point. No data has been sent anywhere yet.',
          },
          {
            step: '2',
            color: 'bg-indigo-500',
            title: 'rsIDs are extracted and sent for lookup',
            icon: Server,
            desc: 'GENEie extracts the list of rsIDs from your file (e.g. "rs429358", "rs334") and sends only those identifiers — not your genotype data — to our backend to look up ClinVar and GWAS records. rsIDs are public identifiers that carry no personal information on their own.',
          },
          {
            step: '3',
            color: 'bg-indigo-500',
            title: 'Genotype matching happens in your browser',
            icon: Eye,
            desc: 'The ClinVar and GWAS records are returned to your browser, where they are matched against your genotypes entirely client-side. Your actual A/T/G/C genotype data never leaves your device during analysis.',
          },
          {
            step: '4',
            color: 'bg-amber-500',
            title: 'Optional: Save your analysis (requires sign-in)',
            icon: Lock,
            desc: 'If you choose to save, your raw file is compressed (gzip) and uploaded to the cloud under your user account. Your analysis results are also saved so you can reload them later. This step is entirely optional.',
          },
          {
            step: '5',
            color: 'bg-emerald-500',
            title: 'Results displayed — analysis is complete',
            icon: ShieldCheck,
            desc: 'Your results are rendered in your browser. If you did not save, no copy of your data exists anywhere except your own device\'s memory, which is cleared when you close the tab.',
          },
        ].map(({ step, color, title, icon: Icon, desc }) => (
          <div key={step} className="flex gap-3 p-3.5 bg-white rounded-lg border border-slate-200">
            <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold text-white ${color}`}>
              {step}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Icon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                <p className="text-xs font-bold text-slate-800">{title}</p>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* What is and isn't stored */}
    <div>
      <SectionHeader icon={HardDrive} color="bg-slate-600">What is and isn't stored</SectionHeader>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <p className="text-xs font-bold text-emerald-800 mb-2 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Never stored (without saving)
          </p>
          <ul className="space-y-1.5 text-xs text-emerald-700">
            {[
              'Your raw DNA file',
              'Your individual genotypes (A/T/G/C)',
              'Your analysis results',
              'Which rsIDs were in your file',
            ].map(item => (
              <li key={item} className="flex items-start gap-1.5">
                <span className="text-emerald-400 mt-0.5">✓</span>{item}
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-xs font-bold text-amber-800 mb-2 flex items-center gap-1.5">
            <Lock className="w-3.5 h-3.5" /> Stored only if you save
          </p>
          <ul className="space-y-1.5 text-xs text-amber-700">
            {[
              'Compressed raw DNA file (Firebase Storage)',
              'Analysis results JSON',
              'File name and upload date',
              'Linked to your account only',
            ].map(item => (
              <li key={item} className="flex items-start gap-1.5">
                <span className="text-amber-400 mt-0.5">→</span>{item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>

    {/* Deleting your data */}
    <div>
      <SectionHeader icon={Trash} color="bg-rose-500">Deleting your data</SectionHeader>
      <p className="text-slate-600 mb-3 leading-relaxed">
        If you have saved analyses, you can delete them at any time from <strong>My Files</strong> in the
        account menu. Deletion removes both the compressed raw file from Firebase Storage and the
        analysis record from the database — no copies are retained.
      </p>
      <Callout icon={Info} color="slate" title="">
        If you used GENEie without uploading data, no data was stored and there is nothing to delete.
      </Callout>
    </div>

    {/* Third parties */}
    <div>
      <SectionHeader icon={Server} color="bg-violet-500">Third-party services used</SectionHeader>
      <div className="space-y-2">
        {[
          {
            name: 'Firebase (Google)',
            purpose: 'Authentication and file storage for signed-in users',
            privacy: 'https://firebase.google.com/support/privacy',
            data: 'Email address, compressed DNA file (if saved)',
          },
          {
            name: 'GENEie API (Google Cloud Run)',
            purpose: 'ClinVar and GWAS database lookups',
            privacy: 'https://cloud.google.com/terms/cloud-privacy-notice',
            data: 'rsID list only — no genotypes, no personal identifiers',
          },
          {
            name: 'Anthropic Claude API',
            purpose: 'AI assistant responses inside variant detail panels',
            privacy: 'https://www.anthropic.com/legal/privacy',
            data: 'SNP context (rsID, gene, conditions) — no raw genotype data',
          },
        ].map(({ name, purpose, privacy, data }) => (
          <div key={name} className="p-3 bg-white rounded-lg border border-slate-200">
            <div className="flex items-start justify-between gap-2 mb-1">
              <p className="text-xs font-bold text-slate-800">{name}</p>
              <a href={privacy} target="_blank" rel="noopener noreferrer"
                className="text-xs text-indigo-500 hover:text-indigo-700 flex items-center gap-0.5 flex-shrink-0">
                Privacy policy <ExternalLink className="w-2.5 h-2.5" />
              </a>
            </div>
            <p className="text-xs text-slate-500 mb-1">{purpose}</p>
            <p className="text-xs text-slate-400"><span className="font-medium text-slate-500">Data shared:</span> {data}</p>
          </div>
        ))}
      </div>
    </div>

    {/* FAQ */}
    <div>
      <SectionHeader icon={Info} color="bg-slate-500">Common questions</SectionHeader>
      <div className="space-y-2">
        <Expandable title="Can GENEie staff see my genetic data?">
          <p>If you have saved your file, it is stored in Firebase Storage and is technically accessible to account administrators. In practice, we do not access user files. If you are concerned about this, use GENEie without signing in — your data will never leave your browser and no one can access it.</p>
        </Expandable>
        <Expandable title="Is my data used to train AI models?">
          <p>No. Your genetic data is not used by GENEie nor its partners to train any AI model. AI message requests include only the SNP context (rsID, gene name, associated conditions) — not your raw genotype data, and our terms prohibit using API inputs for model training without consent.</p>
        </Expandable>
        <Expandable title="What happens if I delete my account?">
          <p>Deleting your account will remove your authentication record. You should manually delete your saved files from My Files before deleting your account to ensure your DNA data is also removed from storage.</p>
        </Expandable>
        <Expandable title="Is my connection to GENEie encrypted?">
          <p>Yes. All communication between your browser and GENEie's servers uses HTTPS/TLS encryption. Files stored in the cloud are encrypted at rest using AES-256.</p>
        </Expandable>
      </div>
    </div>

  </div>
);


// ─── Glossary page ────────────────────────────────────────────────────────────
const GLOSSARY_TERMS = [
  { term: 'Allele', category: 'Genetics basics', definition: 'One of the possible versions of a gene or DNA sequence at a specific location in the genome. Most people carry two alleles at each position — one inherited from each parent. For example, at rs429358, the two possible alleles are C and T.' },
  { term: 'Beta coefficient', category: 'Statistics', definition: 'A number reported by GWAS studies of quantitative traits (e.g. BMI, cholesterol). It represents the average change in the trait per additional risk allele, measured in standard deviation units. A beta of 0.08 means each copy of the allele shifts the measurement by 0.08 SDs — not the same as an odds ratio and cannot be expressed as a percentage.' },
  { term: 'Carrier', category: 'Genetics basics', definition: 'A person who carries one copy of a variant associated with a recessive condition. Carriers typically do not develop the condition themselves but can pass the variant to their children. If two carriers have children together, each child has a 25% chance of inheriting both copies and being affected.' },
  { term: 'ClinVar', category: 'Databases', definition: "A public database maintained by the US National Center for Biotechnology Information (NCBI) that archives interpretations of genetic variants submitted by clinical laboratories and research groups. Each submission includes the variant, the associated condition, the classification (pathogenic, benign, etc.), and supporting evidence. GENEie uses ClinVar as its primary source for clinical classifications." },
  { term: 'Confidence interval (CI)', category: 'Statistics', definition: 'A range of values around a study estimate that likely contains the true value. A 95% CI of [1.1-1.5] means the study is 95% confident the true odds ratio falls between 1.1 and 1.5. Wide CIs indicate uncertain estimates, usually from smaller studies.' },
  { term: 'dbSNP', category: 'Databases', definition: "The NCBI database of single nucleotide polymorphisms. Every common genetic variant is assigned a unique rsID in dbSNP, which is used as the universal identifier across all genetic databases including GENEie, ClinVar, and the GWAS Catalog." },
  { term: 'Genotype', category: 'Genetics basics', definition: 'The specific pair of alleles a person carries at a given position. Written as two letters — e.g. CT, AA, or GG — representing the allele inherited from each parent. In GENEie, your genotype is shown on every result card.' },
  { term: 'GWAS', category: 'Research methods', definition: 'Genome-Wide Association Study. A type of research study that scans the genomes of thousands of people to identify genetic variants statistically associated with a particular trait or disease. GWAS identifies correlations, not causes — a variant associated with a disease may be near, but not necessarily the actual causal variant.' },
  { term: 'GWAS Catalog', category: 'Databases', definition: 'A curated database maintained by the European Bioinformatics Institute (EMBL-EBI) and NHGRI that collects published GWAS findings. For each association, it records the variant, trait, odds ratio or beta, p-value, study population, and source publication. GENEie queries the GWAS Catalog to supplement ClinVar data.' },
  { term: 'Hemizygous', category: 'Genetics basics', definition: 'Having only one copy of a gene rather than the usual two, most commonly referring to X-linked genes in males (who have one X and one Y chromosome). A hemizygous male with a pathogenic variant on his X chromosome will typically be affected, unlike a female who has a second X chromosome that may carry a normal copy.' },
  { term: 'Heterozygous', category: 'Genetics basics', definition: 'Carrying two different alleles at a given position — one from each parent. For example, a genotype of CT is heterozygous. The effect of being heterozygous depends on the variant: some conditions require two copies (recessive), others require only one (dominant).' },
  { term: 'Homozygous', category: 'Genetics basics', definition: 'Carrying two identical alleles at a given position. A genotype of CC or TT is homozygous. For recessive conditions, being homozygous for the risk allele typically means both gene copies are affected. For dominant conditions, being homozygous may increase severity compared to heterozygous.' },
  { term: 'Linkage disequilibrium (LD)', category: 'Genetics basics', definition: 'The tendency for alleles at nearby positions on the same chromosome to be inherited together more often than expected by chance. When a GWAS study identifies an associated variant, the true causal variant may not be that exact position — it may be a nearby variant in LD with it. This is why GWAS associations are starting points for research, not definitive causal findings.' },
  { term: 'Likely Pathogenic', category: 'Classifications', definition: 'A ClinVar classification meaning there is strong evidence that a variant causes disease, but the evidence is not yet sufficient to reach the highest confidence level of Pathogenic. These classifications are updated over time as more evidence accumulates.' },
  { term: 'Minor allele frequency (MAF)', category: 'Statistics', definition: 'The proportion of chromosomes in a population that carry the less common allele at a given position. A MAF of 0.05 means 5% of chromosomes in the studied population carry that allele. Rare variants with low MAF are not well represented in GWAS studies, which is why ClinVar is the primary source for rare disease variants.' },
  { term: 'Odds ratio (OR)', category: 'Statistics', definition: 'A measure of association used in GWAS studies. An OR of 1.3 means people carrying the risk allele had 1.3 times the odds of developing the condition compared to those without it. An OR is not a probability — an OR of 1.3 with a baseline disease rate of 5% means your rate is approximately 6.4%, not 30%.' },
  { term: 'p-value', category: 'Statistics', definition: 'A measure of statistical significance. In GWAS, the standard threshold is p < 5e-8, which accounts for the millions of statistical tests performed across the genome. A p-value below this threshold means the association is unlikely to be a false positive. It does not measure the size or clinical importance of the effect.' },
  { term: 'Pathogenic', category: 'Classifications', definition: 'The highest confidence ClinVar classification, indicating strong and consistent evidence from multiple independent sources that a variant causes a specific disease. Pathogenic does not mean certain disease — penetrance (whether the variant actually causes disease in a given individual) can still vary.' },
  { term: 'Penetrance', category: 'Genetics basics', definition: 'The proportion of people with a given genotype who actually develop the associated condition. A variant has high penetrance if most carriers develop the condition (e.g. BRCA1 mutations and breast cancer), and low penetrance if only a small fraction do. Many pathogenic variants have incomplete penetrance, meaning a positive result is not a diagnosis.' },
  { term: 'Pharmacogenomics', category: 'Research methods', definition: "The study of how genetic variants affect a person's response to drugs — including how quickly they metabolise a medication, how effective it is, and whether they are at increased risk of side effects. GENEie labels variants with drug-response implications using the Drug Response category." },
  { term: 'Polygenic risk score (PRS)', category: 'Research methods', definition: "A score calculated from hundreds or thousands of small-effect genetic variants that together predict a person's genetic predisposition to a trait or condition. GENEie does not currently calculate PRS — it reports individual variant associations. PRS requires careful calibration for the specific population it will be applied to." },
  { term: 'Risk allele', category: 'Genetics basics', definition: "The specific allele that a GWAS study found to be associated with increased risk of a trait or condition. The same allele that is the risk allele in one study may be protective or neutral in a different population. In GENEie's detail view, the risk allele is shown next to each GWAS association row." },
  { term: 'rsID', category: 'Databases', definition: 'Reference SNP identifier — the unique ID assigned to each known genetic variant in the NCBI dbSNP database. All rsIDs begin with "rs" followed by a number (e.g. rs429358). rsIDs are used as the universal identifier across all genetic databases and in raw data files from consumer testing companies.' },
  { term: 'SNP', category: 'Genetics basics', definition: 'Single Nucleotide Polymorphism — a position in the genome where a single DNA base (A, T, G, or C) differs between individuals. SNPs are the most common type of genetic variation. Consumer genetic testing chips typically measure 600,000 to 900,000 SNPs across the genome.' },
  { term: 'Uncertain Significance (VUS)', category: 'Classifications', definition: 'Variant of Uncertain Significance — a ClinVar classification meaning there is not yet enough evidence to classify the variant as either pathogenic or benign. VUS classifications are common and are regularly re-evaluated as new evidence emerges. A VUS is not a diagnosis and should not be treated as one.' },
  { term: 'Variant', category: 'Genetics basics', definition: "Any difference between an individual's DNA sequence and a reference genome. In common usage, variant and SNP are often used interchangeably in the context of consumer genetic testing, though technically a variant can also refer to insertions, deletions, or structural changes." },
  { term: 'Zygosity', category: 'Genetics basics', definition: 'Whether a person carries one or two copies of a particular allele. Heterozygous = one copy (e.g. CT). Homozygous = two copies of the same allele (e.g. CC or TT). Zygosity affects whether a variant expresses a dominant or recessive effect.' },
];

const GLOSSARY_CATEGORIES = ['All', 'Genetics basics', 'Statistics', 'Classifications', 'Databases', 'Research methods'];

const GlossaryPage = () => {
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = GLOSSARY_TERMS.filter(t => {
    const matchesCategory = activeCategory === 'All' || t.category === activeCategory;
    const q = query.trim().toLowerCase();
    const matchesQuery = !q || t.term.toLowerCase().includes(q) || t.definition.toLowerCase().includes(q);
    return matchesCategory && matchesQuery;
  });

  const grouped = filtered.reduce((acc, term) => {
    const letter = term.term[0].toUpperCase();
    if (!acc[letter]) acc[letter] = [];
    acc[letter].push(term);
    return acc;
  }, {});

  const categoryColors = {
    'Genetics basics':  'bg-indigo-50 text-indigo-700 border-indigo-200',
    'Statistics':       'bg-amber-50  text-amber-700  border-amber-200',
    'Classifications':  'bg-rose-50   text-rose-700   border-rose-200',
    'Databases':        'bg-violet-50 text-violet-700 border-violet-200',
    'Research methods': 'bg-teal-50   text-teal-700   border-teal-200',
  };

  return (
    <div className="space-y-5 text-sm">
      <div>
        <p className="text-slate-600 leading-relaxed mb-4">
          Definitions for the genetic, statistical, and database terminology used throughout GENEie.
          Use the search box or category filters to find a specific term.
        </p>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search terms or definitions..."
            className="w-full pl-8 pr-3 py-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 transition-colors"
          />
          {query && (
            <button onClick={() => setQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {GLOSSARY_CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                activeCategory === cat
                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {(query || activeCategory !== 'All') && (
        <p className="text-xs text-slate-400">
          {filtered.length} term{filtered.length !== 1 ? 's' : ''} found
          {activeCategory !== 'All' ? ` in ${activeCategory}` : ''}
          {query ? ` matching "${query}"` : ''}
        </p>
      )}

      {filtered.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-slate-400 text-sm">No terms match your search.</p>
          <button onClick={() => { setQuery(''); setActiveCategory('All'); }} className="mt-2 text-xs text-indigo-500 hover:text-indigo-700">
            Clear filters
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.keys(grouped).sort().map(letter => (
            <div key={letter}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-black text-slate-300 w-5 text-center">{letter}</span>
                <div className="flex-1 h-px bg-slate-100" />
              </div>
              <div className="space-y-2 ml-7">
                {grouped[letter].map(({ term, category, definition }) => (
                  <div key={term} className="bg-white rounded-lg border border-slate-200 p-3.5">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <p className="text-sm font-bold text-slate-900">{term}</p>
                      <span className={`px-1.5 py-0.5 rounded border text-xs font-medium flex-shrink-0 ${categoryColors[category] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                        {category}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 leading-relaxed">{definition}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Knowledge Center modal ──────────────────────────────────────────────────
const PAGES = [
  { id: 'getting-data',   label: 'Getting Your Data',    icon: Download },
  { id: 'understanding',  label: 'Understanding Results', icon: BookOpen },
  { id: 'privacy',        label: 'Privacy & Security',    icon: Lock },
  { id: 'glossary',       label: 'Glossary',              icon: FileText },
];

const KnowledgeCenter = ({ onClose }) => {
  const [activePage, setActivePage] = useState('getting-data');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-900 via-indigo-900 to-slate-900 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-indigo-500/30 rounded-lg flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-indigo-200" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white">Knowledge Center</h2>
              <p className="text-xs text-indigo-300">Learn how to read your results</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-300" />
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Sidebar nav */}
          <nav className="w-52 flex-shrink-0 border-r border-slate-200 bg-slate-50 p-3 space-y-1">
            {PAGES.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActivePage(id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-colors text-sm font-medium ${
                  activePage === id
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                {label}
              </button>
            ))}

            {/* Placeholder for future pages */}
            <div className="pt-3 mt-3 border-t border-slate-200">
              <p className="text-xs text-slate-400 px-3 mb-2 font-medium uppercase tracking-wide">Coming soon</p>
              {['Ancestry & GWAS', 'Drug Response'].map(name => (
                <div key={name} className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-slate-300 text-xs cursor-not-allowed">
                  <div className="w-3.5 h-3.5 rounded bg-slate-200 flex-shrink-0" />
                  {name}
                </div>
              ))}
            </div>
          </nav>

          {/* Content */}
          <main className="flex-1 overflow-y-auto p-6">
            {activePage === 'getting-data'  && <GettingYourDataPage />}
            {activePage === 'understanding' && <UnderstandingResultsPage />}
            {activePage === 'privacy'       && <PrivacySecurityPage />}
            {activePage === 'glossary'      && <GlossaryPage />}
          </main>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeCenter;